// src/pages/supplier/MarketIntelligencePage.jsx
// Phase 2 — M2: Supplier market intelligence dashboard

import { useEffect, useState } from 'react'
import { marketInsightsApi } from '../../api/services'

const POSITION_COLORS = {
  budget:       { bg: '#DCFCE7', text: '#166534', label: 'Budget ↓' },
  competitive:  { bg: '#DBEAFE', text: '#1E40AF', label: 'Competitive ✓' },
  premium:      { bg: '#FEF3C7', text: '#92400E', label: 'Premium ↑' },
  luxury:       { bg: '#F3E8FF', text: '#6B21A8', label: 'Luxury ★' },
}

function PriceBadge({ position }) {
  const c = POSITION_COLORS[position] || { bg: '#F3F4F6', text: '#6B7280', label: position || '—' }
  return (
    <span style={{ background: c.bg, color: c.text, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
      {c.label}
    </span>
  )
}

function TrendBar({ value, max = 100 }) {
  const pct = Math.min((value / max) * 100, 100)
  const color = pct > 70 ? '#10B981' : pct > 40 ? '#F59E0B' : '#EF4444'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#F3F4F6', borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color, minWidth: 30 }}>{Math.round(value)}</span>
    </div>
  )
}

export default function MarketIntelligencePage() {
  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [tab, setTab]           = useState('products')
  const [trends, setTrends]     = useState(null)

  useEffect(() => {
    Promise.all([
      marketInsightsApi.report(),
      marketInsightsApi.trends(),
    ]).then(([rRes, tRes]) => {
      setData(rRes.data?.data ?? rRes.data)
      setTrends(tRes.data?.data ?? tRes.data)
    }).finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading market data…</div>
  if (!data)   return <div style={{ padding: 40, textAlign: 'center', color: '#EF4444' }}>Could not load market insights</div>

  const report  = data.report  || []
  const p_trends = data.platform_trends || trends || {}

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 0 48px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.8rem', color: '#1A1A2E', margin: '0 0 4px' }}>Market Intelligence</h1>
        <p style={{ color: '#9CA3AF', margin: 0, fontSize: '0.88rem' }}>Benchmark your pricing against the market and discover demand signals</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#F3F4F6', borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 24 }}>
        {[['products', 'My Products'], ['trends', 'Platform Trends'], ['searches', 'Demand Signals']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem',
            background: tab === key ? '#fff' : 'transparent',
            color: tab === key ? '#FF3D7F' : '#6B7280',
            boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          }}>{label}</button>
        ))}
      </div>

      {/* ── Products Tab ── */}
      {tab === 'products' && (
        <div>
          {report.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '48px', textAlign: 'center', color: '#9CA3AF' }}>
              <p style={{ fontSize: '2rem', marginBottom: 8 }}>📊</p>
              <p>No published products found. List your products to see market benchmarks.</p>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #F0F0F0' }}>
                    {['Product', 'Your Price', 'Market Avg', 'Min / Max', 'Position', 'Demand', '7d Views', '30d Orders'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.map(row => (
                    <tr key={row.product_id} style={{ borderBottom: '1px solid #F9FAFB' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1A1A2E', maxWidth: 200 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.product_name}</div>
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: '#FF3D7F' }}>₹{row.your_price?.toLocaleString('en-IN')}</td>
                      <td style={{ padding: '12px 14px', color: '#374151' }}>
                        {row.market_avg ? `₹${Math.round(row.market_avg).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#6B7280', fontSize: '0.78rem' }}>
                        {row.market_min && row.market_max
                          ? `₹${Math.round(row.market_min).toLocaleString('en-IN')} – ₹${Math.round(row.market_max).toLocaleString('en-IN')}`
                          : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}><PriceBadge position={row.price_position} /></td>
                      <td style={{ padding: '12px 14px', minWidth: 120 }}>
                        <TrendBar value={row.demand_score ?? 0} />
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: '#374151' }}>{row.views_7d?.toLocaleString()}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: '#374151' }}>{row.orders_30d?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pricing tips */}
          <div style={{ marginTop: 20, background: '#FFF0F5', border: '1px solid #FFD6E7', borderRadius: 14, padding: '16px 20px' }}>
            <p style={{ fontWeight: 700, color: '#FF3D7F', margin: '0 0 8px', fontSize: '0.88rem' }}>💡 Pricing Tips</p>
            <ul style={{ margin: 0, paddingLeft: 18, color: '#6B7280', fontSize: '0.82rem', lineHeight: 1.7 }}>
              <li><b>Budget</b> — You're priced below market. Consider raising your price or highlighting unique quality.</li>
              <li><b>Competitive</b> — You're in the sweet spot. Good visibility in search results.</li>
              <li><b>Premium</b> — Buyers expect superior quality/delivery at this price point. Add trust signals.</li>
              <li><b>Luxury</b> — Ensure exceptional product photos, detailed descriptions, and fast shipping.</li>
            </ul>
          </div>
        </div>
      )}

      {/* ── Platform Trends Tab ── */}
      {tab === 'trends' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Top categories */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: 24 }}>
            <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', margin: '0 0 16px' }}>🔥 Top Categories (7d GMV)</h3>
            {(p_trends.top_categories || []).slice(0, 8).map((cat, i) => (
              <div key={cat.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px dashed #F0F0F0' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontWeight: 800, color: i < 3 ? '#FF3D7F' : '#D1D5DB', fontSize: '0.8rem', width: 16 }}>#{i + 1}</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>{cat.name}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#FF3D7F', fontSize: '0.88rem' }}>₹{(cat.gmv / 100000).toFixed(1)}L</div>
                  <div style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{cat.orders} orders</div>
                </div>
              </div>
            ))}
          </div>

          {/* Avg order value */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: 24 }}>
            <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', margin: '0 0 16px' }}>📈 Platform Metrics (30d)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#FFF0F5', borderRadius: 12, padding: '16px 14px' }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#FF3D7F' }}>
                  {p_trends.avg_order_value ? `₹${Math.round(p_trends.avg_order_value).toLocaleString('en-IN')}` : '—'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 600 }}>Avg Order Value</div>
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151', margin: '0 0 10px' }}>💡 Opportunity Alert</p>
              <p style={{ fontSize: '0.8rem', color: '#6B7280', lineHeight: 1.6, margin: 0 }}>
                Categories with high demand scores and low average order values indicate price-sensitive buyers — ideal for bulk B2B deals.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Demand Signals Tab ── */}
      {tab === 'searches' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: 24 }}>
            <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', margin: '0 0 16px' }}>🔍 Top Buyer Searches (7d)</h3>
            {(p_trends.top_searches || []).length === 0
              ? <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Not enough data yet</p>
              : (p_trends.top_searches || []).map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px dashed #F0F0F0', alignItems: 'center' }}>
                <span style={{ fontWeight: 500, color: '#374151', fontSize: '0.85rem' }}>{s.query}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{s.count} searches</span>
                  <span style={{ fontSize: '0.72rem', background: s.count > 50 ? '#DCFCE7' : '#F3F4F6', color: s.count > 50 ? '#166534' : '#6B7280', padding: '1px 8px', borderRadius: 10, fontWeight: 700 }}>
                    {s.count > 50 ? '🔥 Hot' : 'Rising'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: 24 }}>
            <h3 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A2E', margin: '0 0 16px' }}>❌ Unmet Demand (Zero Results)</h3>
            <p style={{ fontSize: '0.78rem', color: '#9CA3AF', margin: '0 0 14px' }}>Buyers searched for these but found nothing — a sourcing opportunity!</p>
            {(p_trends.zero_searches || []).length === 0
              ? <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>No zero-result searches 🎉</p>
              : (p_trends.zero_searches || []).map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px dashed #F0F0F0', alignItems: 'center' }}>
                <span style={{ fontWeight: 500, color: '#374151', fontSize: '0.85rem' }}>{s.query}</span>
                <span style={{ fontSize: '0.72rem', background: '#FEF3C7', color: '#92400E', padding: '1px 8px', borderRadius: 10, fontWeight: 700 }}>{s.count}× searched</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
